import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { proposalComparisonService } from '../../services/api';
import styles from './CreateComparison.module.css';
import Toast from '../../components/common/Toast/Toast';
import MockAd from '../../components/ads/MockAd/MockAd';

const CreateComparison = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectType = searchParams.get('type');
  
  const [loading, setLoading] = useState(false);
  const [processingAI, setProcessingAI] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [toast, setToast] = useState({ type: '', message: '' });
  
  // Project details
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectLocation, setProjectLocation] = useState('');
  const [projectBudget, setProjectBudget] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // Contractor documents (only 2 contractors)
  const [contractorDocuments, setContractorDocuments] = useState({
    contractor1: [],
    contractor2: []
  });
  
  // Voice recognition states (only for project description)
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);

  // Initialize speech recognition for project description only
  useEffect(() => {
    // Check if browser supports speech recognition
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      // Project description recognition
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';
      
      recognitionInstance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setProjectDescription(transcript);
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setToast({
          type: 'error',
          message: `Speech recognition error: ${event.error}`
        });
        setIsRecording(false);
      };
      
      recognitionInstance.onend = () => {
        setIsRecording(false);
      };
      
      setRecognition(recognitionInstance);
    }
  }, []);

  // Handle voice recording for project description
  const toggleRecording = () => {
    if (!recognition) {
      setToast({
        type: 'error',
        message: 'Speech recognition is not supported in your browser'
      });
      return;
    }
    
    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
    } else {
      recognition.start();
      setIsRecording(true);
      setToast({
        type: 'info',
        message: 'Listening... Speak now'
      });
    }
  };

  // Set default project name based on type
  useEffect(() => {
    if (projectType && !projectName) {
      const typeNames = {
        kitchen: 'Kitchen Renovation',
        bathroom: 'Bathroom Renovation',
        roofing: 'Roofing Project',
        landscaping: 'Landscaping Project',
        flooring: 'Flooring Project',
        painting: 'Painting Project'
      };
      setProjectName(typeNames[projectType] || 'Home Improvement Project');
    }
  }, [projectType, projectName]);

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    setSelectedDocuments([...selectedDocuments, ...files]);
  };

  const removeDocument = (index) => {
    const updatedDocs = [...selectedDocuments];
    updatedDocs.splice(index, 1);
    setSelectedDocuments(updatedDocs);
  };

  const handleContractorFileUpload = (contractorKey, e) => {
    const files = Array.from(e.target.files);
    setContractorDocuments(prev => ({
      ...prev,
      [contractorKey]: [...prev[contractorKey], ...files]
    }));
  };

  const removeContractorFile = (contractorKey, fileIndex) => {
    setContractorDocuments(prev => ({
      ...prev,
      [contractorKey]: prev[contractorKey].filter((_, index) => index !== fileIndex)
    }));
  };

  // Generate or get guest session ID - only when actually submitting
  const getOrCreateGuestId = () => {
    // Try to get existing guest ID from storage
    let guestId = localStorage.getItem('guestSessionId') || sessionStorage.getItem('guestSessionId');
    
    if (!guestId) {
      // Generate new guest ID only when this function is called during submission
      guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Store in both storages for persistence
      localStorage.setItem('guestSessionId', guestId);
      sessionStorage.setItem('guestSessionId', guestId);
      
      // Also store creation timestamp
      localStorage.setItem('guestSessionCreated', new Date().toISOString());
      
      console.log('Created new guest session ID:', guestId);
    } else {
      console.log('Using existing guest session ID:', guestId);
    }
    
    return guestId;
  };

  const handleSubmit = async () => {
    // Declare progress interval variable at function scope so it can be cleared in catch block
    let progressInterval = null;
    
    // Validation
    if (!projectName) {
      setToast({
        type: 'error',
        message: 'Please enter a project name'
      });
      return;
    }

    // Check if user has provided either project description OR uploaded documents
    const hasDescription = projectDescription && projectDescription.trim().length > 0;
    const hasDocuments = selectedDocuments.length > 0;
    
    if (!hasDescription && !hasDocuments) {
      setToast({
        type: 'error',
        message: 'Please either provide a project description or upload project documents. Our AI needs some understanding of your project to provide accurate analysis.'
      });
      return;
    }

    // Check if at least 2 contractors have files uploaded
    const contractorsWithFiles = Object.values(contractorDocuments).filter(docs => docs.length > 0).length;
    if (contractorsWithFiles < 2) {
      setToast({
        type: 'error',
        message: 'Please upload files for both contractors to create a comparison'
      });
      return;
    }

    try {
      // Prepare contractors data from uploaded files
      const contractors = [];
      Object.entries(contractorDocuments).forEach(([key, documents], index) => {
        if (documents.length > 0) {
          contractors.push({
            name: `Contractor ${index + 1}`,
            email: '',
            phone: '',
            company: '',
            behavioralNotes: '',
            appearance: 0,
            organization: 0,
            competency: 0,
            documents: documents
          });
        }
      });

      if (contractors.length < 2) {
        setToast({
          type: 'error',
          message: 'Please upload files for both contractors'
        });
        return;
      }
      
      setLoading(true);
      
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      const isAuthenticated = !!token;
      
      let projectData;
      
      if (isAuthenticated) {
        // Authenticated user flow
        projectData = {
          projectName,
          projectDescription,
          projectLocation,
          projectBudget,
          contractors: contractors.map(contractor => ({
            name: contractor.name,
            email: contractor.email,
            phone: contractor.phone,
            company: contractor.company,
            behavioralNotes: contractor.behavioralNotes,
            appearance: contractor.appearance,
            organization: contractor.organization,
            competency: contractor.competency
          })),
          isGuestUser: false
        };
      } else {
        // Guest user flow
        const guestSessionId = getOrCreateGuestId();
        
        projectData = {
          projectName,
          projectDescription,
          projectLocation,
          projectBudget,
          contractors: contractors.map(contractor => ({
            name: contractor.name,
            email: contractor.email,
            phone: contractor.phone,
            company: contractor.company,
            behavioralNotes: contractor.behavioralNotes,
            appearance: contractor.appearance,
            organization: contractor.organization,
            competency: contractor.competency
          })),
          isGuestUser: true,
          guestSessionId: guestSessionId
        };
      }
      
      // Create the comparison entry in the database
      console.log('=== FRONTEND: ABOUT TO CALL API ===');
      console.log('Project data being sent:', JSON.stringify(projectData, null, 2));
      console.log('Guest session ID from storage:', localStorage.getItem('guestSessionId'));
      console.log('=====================================');
      
      const response = await proposalComparisonService.createComparison(projectData);
      
      if (response.data && response.data.status === 'success') {
        const comparisonId = response.data.data.id;
        
        // Upload project documents first (SOW files)
        const uploadPromises = [];
        
        // Handle project documents or create description file
        if (selectedDocuments.length > 0 || hasDescription) {
          const projectFormData = new FormData();
          projectFormData.append('projectName', projectName);
          
          // Add all uploaded project document files
          for (let i = 0; i < selectedDocuments.length; i++) {
            console.log(`Adding project file ${i + 1}:`, selectedDocuments[i].name);
            projectFormData.append('files', selectedDocuments[i]);
          }
          
          // If user provided description but no files, create a text file from description
          if (hasDescription && selectedDocuments.length === 0) {
            // const descriptionContent = `Project: ${projectName}\n\nDescription:\n${projectDescription}\n\nLocation: ${projectLocation || 'Not specified'}\nBudget: ${projectBudget || 'Not specified'}\n\nGenerated on: ${new Date().toLocaleString()}`;
            const descriptionContent = projectDescription;
            const descriptionBlob = new Blob([descriptionContent], { type: 'text/plain' });
            const descriptionFile = new File([descriptionBlob], `${projectName.replace(/[^a-zA-Z0-9]/g, '_')}_project_description.txt`, { type: 'text/plain' });
            
            console.log('Creating description file:', descriptionFile.name);
            projectFormData.append('files', descriptionFile);
          }
          
          console.log('Project documents FormData prepared, making upload request...');
          
          // Upload project files using "project" as contractor parameter (routes to sow folder)
          uploadPromises.push(
            proposalComparisonService.uploadContractorFiles(
              comparisonId, 
              "project", 
              projectFormData
            ).then(response => {
              console.log(`Project documents upload successful:`, response);
              return response;
            }).catch(error => {
              console.error(`Error uploading project documents:`, error);
              return null;
            })
          );
        }
        
        // Upload documents for each contractor
        for (let i = 0; i < contractors.length; i++) {
          const contractor = contractors[i];
          if (contractor.documents && contractor.documents.length > 0) {
            const formData = new FormData();
            formData.append('projectName', projectName);
            formData.append('contractor', contractor.name);
            formData.append('company', contractor.company || '');
            
            // Add all document files
            for (let j = 0; j < contractor.documents.length; j++) {
              console.log(`Adding file ${j + 1}:`, contractor.documents[j].name);
              formData.append('files', contractor.documents[j]);
            }
            
            console.log('FormData prepared, making upload request...');
            
            // Use standardized contractor folder naming (contractor_1, contractor_2, etc.)
            const contractorFolderName = `contractor_${i + 1}`;
            
            // Upload files
            uploadPromises.push(
              proposalComparisonService.uploadContractorFiles(
                comparisonId, 
                contractorFolderName, 
                formData
              ).then(response => {
                console.log(`Upload successful for ${contractor.name}:`, response);
                return response;
              }).catch(error => {
                console.error(`Error uploading files for ${contractor.name}:`, error);
                return null;
              })
            );
          }
        }
        
        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          await Promise.all(uploadPromises);
        }
        
        setLoading(false);
        setProcessingAI(true);
        setProcessingProgress(5);
        
        console.log('=== FILES UPLOADED, STARTING AI PROCESSING ===');
        
        // Realistic progress simulation - slow increase over 30 seconds, then stop at 99%
        progressInterval = setInterval(() => {
          setProcessingProgress(prev => {
            // Slow progress for first 30 seconds (reaches ~95%)
            if (prev < 95) {
              return Math.min(prev + Math.random() * 3, 95);
            }
            // After 30 seconds, gradually approach 99% but never reach 100%
            if (prev < 99) {
              return Math.min(prev + 0.5, 99);
            }
            // Stay at 99% until actual completion
            return 99;
          });
        }, 1000); // Update every 1 second for smoother animation
        
        // Start AI processing after files are uploaded
        try {
          setToast({
            type: 'info',
            message: 'Files uploaded successfully! Starting AI analysis...'
          });
          
          console.log('Starting AI processing API call...');
          
          const aiResponse = await proposalComparisonService.startAIProcessing(comparisonId);
          
          console.log('AI processing API call completed:', aiResponse);
          
          // Stop the progress simulation and set to 100%
          clearInterval(progressInterval);
          setProcessingProgress(100);
          
          if (aiResponse.data && aiResponse.data.status === 'success') {
            console.log('✅ AI processing API call completed:', aiResponse.data);
            
            // Check if this is a premium user with background processing
            if (aiResponse.data.data && aiResponse.data.data.isPremiumAnalysis === true && aiResponse.data.data.processingInBackground === true) {
              console.log('🌟 Premium user detected - background processing started');
              
              // Stop the progress immediately and reset
              clearInterval(progressInterval);
              setProcessingAI(false);
              setProcessingProgress(0);
              
              setToast({
                type: 'info',
                message: `🌟 Premium Analysis Started! ${aiResponse.data.message || 'You will receive an email notification when your detailed analysis is complete (typically 3-5 minutes).'}`
              });
              
                             // Navigate immediately since analysis will continue in background
               // Check if user is authenticated to determine correct navigation
               const token = localStorage.getItem('token');
               setTimeout(() => {
                 if (token) {
                   // Authenticated premium user
                   navigate(`/homeowner/comparison/${comparisonId}`);
                 } else {
                   // Guest premium user (unlikely but handle it)
                   navigate(`/homeowner/comparisons/${comparisonId}?guest=true`);
                 }
               }, 3000);
              
            } else {
              // Regular guest/free user flow - analysis completed
              console.log('✅ Standard analysis completed successfully');
              
              setToast({
                type: 'success',
                message: 'AI analysis completed successfully! Redirecting to comparison view...'
              });
              
              // Small delay to show 100% completion and success message
              setTimeout(() => {
                navigate(`/homeowner/comparisons/${comparisonId}?guest=true`);
              }, 2000);
            }
          } else {
            throw new Error('Failed to complete AI processing');
          }
        } catch (aiError) {
          clearInterval(progressInterval);
          console.error('Error during AI processing:', aiError);
          
          setProcessingAI(false);
          setProcessingProgress(0);
          
          setToast({
            type: 'error',
            message: `AI analysis failed: ${aiError.response?.data?.message || aiError.message}. Please try again.`
          });
        }
        
      } else {
        throw new Error('Failed to create project');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error creating project:', err);
      
      // Clear all loading states and progress indicators
      setLoading(false);
      setProcessingAI(false);
      setProcessingProgress(0);
      
      // Clear progress interval if it's running
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      // Check if this is a guest limit exceeded error
      if (err.response?.data?.code === 'GUEST_LIMIT_EXCEEDED') {
        setToast({
          type: 'error',
          message: err.response.data.message || 'You\'ve reached your free comparison limit. Create a free account to get one more comparison and unlock premium features!',
          action: {
            label: 'Create Free Account',
            onClick: () => navigate('/signup?role=homeowner')
          }
        });
      } else {
        setToast({
          type: 'error',
          message: err.message || 'Failed to create project'
        });
      }
    }
  };

  // Add useEffect for mobile optimization
  useEffect(() => {
    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0';
    document.head.appendChild(viewportMeta);

    return () => {
      if (document.head.contains(viewportMeta)) {
        document.head.removeChild(viewportMeta);
      }
    };
  }, []);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (isRecording && recognition) {
        recognition.stop();
      }
    };
  }, [isRecording, recognition]);

  const getUploadedContractorsCount = () => {
    return Object.values(contractorDocuments).filter(docs => docs.length > 0).length;
  };

  return (
    <div className={styles.createComparison}>
      {toast && toast.message && (
        <Toast 
          type={toast.type} 
          message={toast.message} 
          onClose={() => setToast({ type: '', message: '' })}
          className={styles.toastContainer}
        />
      )}
      
      {/* AI Processing Overlay */}
      {processingAI && (
        <div className={styles.processingOverlay}>
          <div className={styles.processingModal}>
            <div className={styles.processingIcon}>
              <i className="fas fa-brain fa-3x"></i>
            </div>
            <h3>AI is Analyzing Your Proposals</h3>
            <p>Please don't close this page while we analyze your contractor proposals.</p>
            
            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${processingProgress}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>
                {Math.round(processingProgress)}% Complete
              </span>
            </div>
            
                         <div className={styles.processingSteps}>
               <div className={`${styles.step} ${processingProgress > 30 ? styles.completed : styles.active}`}>
                 <i className="fas fa-file-upload"></i>
                 <span>Processing Files</span>
               </div>
               <div className={`${styles.step} ${processingProgress > 70 ? styles.completed : processingProgress > 30 ? styles.active : ''}`}>
                 <i className="fas fa-search"></i>
                 <span>AI Analyzing Content</span>
               </div>
               <div className={`${styles.step} ${processingProgress > 99 ? styles.completed : processingProgress > 70 ? styles.active : ''}`}>
                 <i className="fas fa-chart-line"></i>
                 <span>Generating Comparison</span>
               </div>
             </div>
          </div>
        </div>
      )}
      
      {/* Page Layout with Sidebar Ads */}
      <div className={styles.pageLayout}>
        {/* Left Sidebar Ad */}
        <div className={styles.leftSidebar}>
          <MockAd placement="sidebar" position="left" />
        </div>
        
        {/* Main Content */}
        <div className={styles.mainContent}>
          <div className={styles.container}>
        <div className={styles.header}>
          <h1>Create Project Comparison</h1>
          <p>Compare contractor proposals and make informed decisions for your home improvement project</p>
        </div>
        
        {/* Simple Process Highlight */}
        <div className={styles.processHighlight}>
          <div className={styles.highlightIcon}>
            <i className="fas fa-rocket"></i>
          </div>
          <div className={styles.highlightContent}>
            <h4>Quick & Simple Process</h4>
            <p>
              <strong>No email required • No account creation • No personal information</strong><br/>
              Just upload your contractor proposals and get instant AI-powered comparison!
            </p>
          </div>
        </div>
        
        <div className={styles.guestUsageIndicator}>
            <div className={styles.usageIcon}>
              <i className="fas fa-info-circle"></i>
            </div>
            <div className={styles.usageContent}>
              <h5>Free Trial Usage</h5>
              <p>
                You're using the free trial. Create a free account to get <strong>one more comparison</strong> and unlock premium features!
              </p>
              <button 
                className={styles.createAccountButton}
                onClick={() => navigate('/signup?role=homeowner')}
              >
                <i className="fas fa-user-plus"></i>
                Create Free Account
              </button>
            </div>
          </div>
          
        {/* Project Details Form */}
        <div className={styles.formSection}>
          <h3>
            <i className="fas fa-info-circle"></i>
            Project Information
          </h3>
          <p>Tell us about your home improvement project</p>
          
          <div className={styles.formContainer}>
            <div className={styles.formGroup}>
              <label htmlFor="projectName" className={styles.fieldLabel}>
                <div className={styles.labelContent}>
                  <i className="fas fa-home"></i>
                  <span>Project Name*</span>
                  <div className={styles.fieldTooltip}>
                    <i className="fas fa-info-circle"></i>
                    <div className={styles.tooltipContent}>
                      A clear project name helps our AI categorize and analyze contractor proposals more accurately
                    </div>
                  </div>
                </div>
              </label>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  id="projectName"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                  required
                />
              </div>
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="projectDescription" className={styles.fieldLabel}>
                <div className={styles.labelContent}>
                  <i className="fas fa-edit"></i>
                  <span>Project Description*</span>
                  <span className={styles.requiredAsterisk}>(Required if no documents uploaded)</span>
                  <div className={styles.fieldTooltip}>
                    <i className="fas fa-info-circle"></i>
                    <div className={styles.tooltipContent}>
                      Either provide a detailed description OR upload project documents. Our AI needs to understand your project to provide accurate contractor comparison.
                    </div>
                  </div>
                </div>
              </label>
              <div className={styles.textareaWrapper}>
                <textarea
                  id="projectDescription"
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  placeholder="Describe your project or tap the microphone to use voice input"
                  rows={4}
                />
                <button 
                  type="button"
                  className={`${styles.voiceButton} ${isRecording ? styles.recording : ''}`}
                  onClick={toggleRecording}
                  title={isRecording ? "Stop recording" : "Start voice input"}
                >
                  <i className={`fas ${isRecording ? 'fa-stop' : 'fa-microphone'}`}></i>
                </button>
              </div>
              {isRecording && (
                <div className={styles.recordingIndicator}>
                  <div className={styles.pulse}></div>
                  <span>Recording... Speak now</span>
                </div>
              )}
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="projectLocation" className={styles.fieldLabel}>
                  <div className={styles.labelContent}>
                    <i className="fas fa-map-marker-alt"></i>
                    <span>Location (Zip Code)</span>
                    <div className={styles.fieldTooltip}>
                      <i className="fas fa-info-circle"></i>
                      <div className={styles.tooltipContent}>
                        Location helps our AI analyze if contractor pricing is reasonable for your area and if they understand local building codes
                      </div>
                    </div>
                  </div>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    id="projectLocation"
                    value={projectLocation}
                    onChange={(e) => setProjectLocation(e.target.value)}
                    placeholder="Enter zip code"
                  />
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="projectBudget" className={styles.fieldLabel}>
                  <div className={styles.labelContent}>
                    <i className="fas fa-dollar-sign"></i>
                    <span>Project Budget</span>
                    <div className={styles.fieldTooltip}>
                      <i className="fas fa-info-circle"></i>
                      <div className={styles.tooltipContent}>
                        Your budget helps our AI evaluate which contractor proposals offer the best value and identify any overpriced or suspiciously low bids
                      </div>
                    </div>
                  </div>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    id="projectBudget"
                    value={projectBudget}
                    onChange={(e) => setProjectBudget(e.target.value)}
                    placeholder="Enter budget amount"
                  />
                </div>
              </div>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.fieldLabel}>
                <div className={styles.labelContent}>
                  <i className="fas fa-file-upload"></i>
                  <span>Project Documents*</span>
                  <span className={styles.requiredAsterisk}>(Required if no description provided)</span>
                  <div className={styles.fieldTooltip}>
                    <i className="fas fa-info-circle"></i>
                    <div className={styles.tooltipContent}>
                      Either upload project documents OR provide a detailed description. Our AI needs to understand your project to provide accurate contractor comparison.
                    </div>
                  </div>
                </div>
              </label>
              <div className={styles.fileUploadContainer}>
                <input
                  type="file"
                  id="projectDocuments"
                  onChange={handleDocumentUpload}
                  multiple
                  className={styles.fileInput}
                />
                <label htmlFor="projectDocuments" className={styles.fileInputLabel}>
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>Choose Files</span>
                </label>
              </div>
              
              {selectedDocuments.length > 0 && (
                <div className={styles.selectedFiles}>
                  <h4>Uploaded Documents:</h4>
                  <ul>
                    {selectedDocuments.map((doc, index) => (
                      <li key={index}>
                        <i className="fas fa-file-alt"></i>
                        <span>{doc.name}</span>
                        <button
                          className={styles.removeButton}
                          onClick={() => removeDocument(index)}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contractor Proposals Section */}
        <div className={styles.contractorSection}>
          <h3>
            <i className="fas fa-file-upload"></i>
            Upload Contractor Proposals
            {/* <div className={styles.fieldTooltip}>
              <i className="fas fa-info-circle"></i>
              <div className={styles.tooltipContent}>
                Upload contractor proposals, quotes, or bids to get AI-powered comparison and analysis. Our AI will analyze pricing, scope, timeline, and quality indicators.
              </div>
            </div> */}
          </h3>
          <p>Upload proposals from both contractors. <span className={styles.requiredNote}>Both contractors are required.</span></p>
          
          {/* Contractor Upload Grid */}
          <div className={styles.contractorUploadGrid}>
            {['contractor1', 'contractor2'].map((contractorKey, index) => (
              <div key={contractorKey} className={styles.contractorUploadCard}>
                <div className={styles.contractorUploadHeader}>
                  <div className={styles.contractorNumber}>
                    {index + 1}
                  </div>
                  <h4>Contractor {index + 1}</h4>
                  {contractorDocuments[contractorKey].length > 0 && (
                    <div className={styles.uploadedBadge}>
                      <i className="fas fa-check-circle"></i>
                      {contractorDocuments[contractorKey].length} file(s)
                    </div>
                  )}
                </div>
                
                <div className={styles.fileUploadContainer}>
                  <input
                    type="file"
                    id={`${contractorKey}Documents`}
                    onChange={(e) => handleContractorFileUpload(contractorKey, e)}
                    multiple
                    className={styles.fileInput}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <label htmlFor={`${contractorKey}Documents`} className={styles.fileUploadDropZone}>
                    <div className={styles.uploadIcon}>
                      <i className="fas fa-cloud-upload-alt"></i>
                    </div>
                    <div className={styles.uploadText}>
                      <span>Drop files here or click to upload</span>
                      <small>PDF, DOC, DOCX, XLS, XLSX, PNG, JPG</small>
                    </div>
                  </label>
                </div>
                
                {contractorDocuments[contractorKey].length > 0 && (
                  <div className={styles.uploadedFilesList}>
                    {contractorDocuments[contractorKey].map((file, fileIndex) => (
                      <div key={fileIndex} className={styles.uploadedFile}>
                        <div className={styles.fileInfo}>
                          <i className="fas fa-file-alt"></i>
                          <span>{file.name}</span>
                        </div>
                        <button
                          className={styles.removeFileButton}
                          onClick={() => removeContractorFile(contractorKey, fileIndex)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className={styles.uploadSummary}>
            <span>Contractors with proposals: <strong>{getUploadedContractorsCount()}/2</strong></span>
            <span className={styles.minimumNote}>Both contractors required to proceed</span>
          </div>
        </div>

        {/* Submit Button */}
        <div className={styles.submitSection}>
          {/* Terms of Service Agreement */}
          <div className={styles.termsAgreement}>
            <p className={styles.termsText}>
              By creating this comparison, you agree to our{' '}
              <Link to="/terms" target="_blank" className={styles.termsLink}>
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" className={styles.termsLink}>
                Privacy Policy
              </Link>
            </p>
          </div>
          
          <button 
            className={styles.submitButton} 
            onClick={handleSubmit}
            disabled={loading || processingAI || getUploadedContractorsCount() < 2}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Uploading Files...
              </>
            ) : processingAI ? (
              <>
                <i className="fas fa-brain fa-spin"></i>
                AI Analyzing Proposals... {Math.round(processingProgress)}%
              </>
            ) : (
              <>
                <i className="fas fa-magic"></i>
                Upload & Start AI Analysis ({getUploadedContractorsCount()} Contractor{getUploadedContractorsCount() !== 1 ? 's' : ''})
              </>
            )}
          </button>
        </div>
          </div>
        </div>
        
        {/* Right Sidebar Ad */}
        <div className={styles.rightSidebar}>
          <MockAd placement="sidebar" position="right" />
        </div>
      </div>
    </div>
  );
};

export default CreateComparison; 